// Reusable responsive SVG Analytics Graph Component
// conformed to Shopify Polaris styling guidelines.

export interface GraphMetric {
	month: number;
	year: number;
	proportionalRevenue: number;
	proportionalExpense: number;
	netProfit: number;
	cumulativeRevenue: number;
	cumulativeExpense: number;
	cumulativeProfit: number;
}

export class OpmAnalyticsGraph extends HTMLElement {
	private _items: GraphMetric[] = [];
	private _viewMode: 'realtime' | 'cumulative' = 'realtime';
	private _hoverIndex: number | null = null;

	static get observedAttributes() {
		return ['view-mode'];
	}

	constructor() {
		super();
		this.style.position = 'relative';
		this.style.display = 'block';
		this.style.width = '100%';
	}

	connectedCallback() {
		this.render();
		this.setupListeners();
	}

	attributeChangedCallback(name: string, oldValue: string, newValue: string) {
		if (oldValue === newValue) return;
		if (name === 'view-mode') {
			this._viewMode = (newValue === 'cumulative' ? 'cumulative' : 'realtime') as any;
			this.render();
		}
	}

	get items(): GraphMetric[] {
		return this._items;
	}

	set items(val: GraphMetric[]) {
		this._items = Array.isArray(val) ? val : [];
		this.render();
	}

	get viewMode(): 'realtime' | 'cumulative' {
		return this._viewMode;
	}

	set viewMode(val: 'realtime' | 'cumulative') {
		this._viewMode = val;
		this.setAttribute('view-mode', val);
		this.render();
	}

	private getMonthName(m: number): string {
		const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		if (m >= 1 && m <= 12) {
			return names[m - 1] as string;
		}
		return String(m);
	}

	private render() {
		// Empty state handling
		if (this._items.length === 0) {
			this.innerHTML = `
				<div style="padding: 3rem; text-align: center; border: 1px dashed var(--p-color-border-subdued); border-radius: 8px;">
					<s-text tone="subdued">No financial data available for this machine.</s-text>
				</div>
			`;
			return;
		}

		const width = 600;
		const height = 300;
		const paddingLeft = 70;
		const paddingRight = 20;
		const paddingTop = 25;
		const paddingBottom = 40;

		const graphWidth = width - paddingLeft - paddingRight;
		const graphHeight = height - paddingTop - paddingBottom;

		// 1. Calculate values based on viewMode
		const points = this._items.map(item => {
			if (this._viewMode === 'cumulative') {
				return {
					rev: item.cumulativeRevenue,
					exp: item.cumulativeExpense,
					month: item.month,
					year: item.year
				};
			}
			return {
				rev: item.proportionalRevenue,
				exp: item.proportionalExpense,
				month: item.month,
				year: item.year
			};
		});

		// 2. Compute range boundaries
		let maxVal = 0;
		points.forEach(p => {
			if (p.rev > maxVal) maxVal = p.rev;
			if (p.exp > maxVal) maxVal = p.exp;
		});

		maxVal = Math.ceil(maxVal * 1.1 / 100) * 100; // add 10% cushion and round
		if (maxVal <= 0) {
			maxVal = 100;
		}

		// 3. Grid line values
		const yGridSteps = 4;
		const yGridLines: number[] = [];
		for (let i = 0; i <= yGridSteps; i++) {
			yGridLines.push((maxVal / yGridSteps) * i);
		}

		// 4. Coordinates mapping function
		const getX = (idx: number) => {
			if (points.length <= 1) return paddingLeft + graphWidth / 2;
			return paddingLeft + (idx / (points.length - 1)) * graphWidth;
		};

		const getY = (val: number) => {
			return paddingTop + graphHeight - (val / maxVal) * graphHeight;
		};

		// 5. Generate grid line SVG markup
		const gridHtml = yGridLines.map(val => {
			const y = getY(val);
			return `
				<line x1="${paddingLeft}" y1="${y}" x2="${width - paddingRight}" y2="${y}" stroke="var(--p-color-border-subdued)" stroke-dasharray="3 3" />
				<text x="${paddingLeft - 10}" y="${y + 4}" fill="var(--p-color-text-subdued)" font-size="10" text-anchor="end">${val.toLocaleString()}</text>
			`;
		}).join('');

		// 6. Generate X labels
		const xLabelsHtml = points.map((p, idx) => {
			const x = getX(idx);
			const label = `${this.getMonthName(p.month)} ${String(p.year).substring(2)}`;
			return `
				<text x="${x}" y="${height - 15}" fill="var(--p-color-text-subdued)" font-size="10" text-anchor="middle">${label}</text>
				<line x1="${x}" y1="${paddingTop + graphHeight}" x2="${x}" y2="${paddingTop + graphHeight + 4}" stroke="var(--p-color-border-subdued)" />
			`;
		}).join('');

		// 7. Shading polygons between lines to highlight profit vs loss intervals
		let polysHtml = '';
		for (let i = 0; i < points.length - 1; i++) {
			const xA = getX(i);
			const yRevA = getY(points[i]!.rev);
			const yExpA = getY(points[i]!.exp);

			const xB = getX(i + 1);
			const yRevB = getY(points[i + 1]!.rev);
			const yExpB = getY(points[i + 1]!.exp);

			const avgRev = (points[i]!.rev + points[i + 1]!.rev) / 2;
			const avgExp = (points[i]!.exp + points[i + 1]!.exp) / 2;
			const isProfit = avgRev >= avgExp;

			const fillColor = isProfit ? 'rgba(16, 124, 65, 0.12)' : 'rgba(209, 52, 56, 0.12)';
			polysHtml += `
				<polygon points="${xA},${yRevA} ${xB},${yRevB} ${xB},${yExpB} ${xA},${yExpA}" fill="${fillColor}" />
			`;
		}

		// 8. Generate Paths for lines
		let revPath = '';
		let expPath = '';
		points.forEach((p, idx) => {
			const x = getX(idx);
			const yRev = getY(p.rev);
			const yExp = getY(p.exp);

			if (idx === 0) {
				revPath = `M ${x} ${yRev}`;
				expPath = `M ${x} ${yExp}`;
			} else {
				revPath += ` L ${x} ${yRev}`;
				expPath += ` L ${x} ${yExp}`;
			}
		});

		// 9. Interactive elements: Hover guide line & Highlight circles
		let hoverGuideHtml = '';
		if (this._hoverIndex !== null && this._hoverIndex >= 0 && this._hoverIndex < points.length) {
			const idx = this._hoverIndex;
			const x = getX(idx);
			const yRev = getY(points[idx]!.rev);
			const yExp = getY(points[idx]!.exp);

			hoverGuideHtml = `
				<line x1="${x}" y1="${paddingTop}" x2="${x}" y2="${paddingTop + graphHeight}" stroke="var(--p-color-border-subdued)" stroke-dasharray="4 4" stroke-width="1" />
				<circle cx="${x}" cy="${yRev}" r="5" fill="#107c41" stroke="white" stroke-width="1.5" />
				<circle cx="${x}" cy="${yExp}" r="5" fill="#d13438" stroke="white" stroke-width="1.5" />
			`;
		}

		this.innerHTML = `
			<div class="opm-analytics-graph-container" style="position: relative; width: 100%;">
				<!-- SVG Canvas -->
				<svg viewBox="0 0 ${width} ${height}" style="width: 100%; height: auto; display: block; overflow: visible;">
					<!-- Axes & Grid -->
					<line x1="${paddingLeft}" y1="${paddingTop}" x2="${paddingLeft}" y2="${paddingTop + graphHeight}" stroke="var(--p-color-border-subdued)" />
					<line x1="${paddingLeft}" y1="${paddingTop + graphHeight}" x2="${width - paddingRight}" y2="${paddingTop + graphHeight}" stroke="var(--p-color-border-subdued)" />
					
					<!-- Grid lines and labels -->
					${gridHtml}
					${xLabelsHtml}

					<!-- Shaded area between lines -->
					${polysHtml}

					<!-- Line paths -->
					<path d="${revPath}" fill="none" stroke="#107c41" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
					<path d="${expPath}" fill="none" stroke="#d13438" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />

					<!-- Guide & Dots on Hover -->
					${hoverGuideHtml}
				</svg>

				<!-- Hover Tooltip overlay -->
				<div class="graph-tooltip" style="
					position: absolute;
					display: none;
					background: var(--p-color-bg-surface, #ffffff);
					border: 1px solid var(--p-color-border-subdued, #e1e3e5);
					border-radius: var(--p-border-radius-200, 8px);
					box-shadow: var(--p-shadow-300, 0 4px 6px rgba(0,0,0,0.1));
					padding: 0.75rem;
					pointer-events: none;
					z-index: 100;
					font-size: 0.825rem;
					min-width: 150px;
					font-family: -apple-system, BlinkMacSystemFont, sans-serif;
					box-sizing: border-box;
				"></div>
			</div>
		`;
	}

	private setupListeners() {
		this.addEventListener('mousemove', (e: MouseEvent) => {
			const container = this.querySelector('.opm-analytics-graph-container') as HTMLElement;
			const svg = this.querySelector('svg');
			const tooltip = this.querySelector('.graph-tooltip') as HTMLElement;
			if (!container || !svg || !tooltip || this._items.length === 0) return;

			const rect = svg.getBoundingClientRect();
			const clientX = e.clientX - rect.left;

			const width = 600;
			const paddingLeft = 70;
			const paddingRight = 20;
			const graphWidth = width - paddingLeft - paddingRight;

			// Translate client X to SVG viewbox coordinate X
			const svgX = (clientX / rect.width) * width;
			const relativeX = svgX - paddingLeft;

			if (relativeX < -15 || relativeX > graphWidth + 15) {
				this.hideTooltip();
				return;
			}

			// Find closest metric point
			const totalPoints = this._items.length;
			let closestIdx = 0;
			let minDiff = Infinity;

			for (let i = 0; i < totalPoints; i++) {
				const ptX = (i / (totalPoints - 1)) * graphWidth;
				const diff = Math.abs(relativeX - ptX);
				if (diff < minDiff) {
					minDiff = diff;
					closestIdx = i;
				}
			}

			if (this._hoverIndex !== closestIdx) {
				this._hoverIndex = closestIdx;
				this.render(); // Redraw guide dots
			}

			// Position and populate Tooltip
			const item = this._items[closestIdx];
			if (!item) return;

			const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
			const monthLabel = `${monthNames[item.month - 1]} ${item.year}`;

			let rev = item.proportionalRevenue;
			let exp = item.proportionalExpense;
			let profit = item.netProfit;

			if (this._viewMode === 'cumulative') {
				rev = item.cumulativeRevenue;
				exp = item.cumulativeExpense;
				profit = item.cumulativeProfit;
			}

			const isProfit = profit >= 0;
			const profitText = isProfit ? 'Profit' : 'Loss';
			const profitTone = isProfit ? 'color: var(--p-color-text-success, #107c41);' : 'color: var(--p-color-text-critical, #d13438);';

			tooltip.innerHTML = `
				<div style="font-weight: 600; margin-bottom: 0.25rem; border-bottom: 1px solid var(--p-color-border-subdued); padding-bottom: 0.25rem;">
					${monthLabel}
				</div>
				<div style="display: flex; justify-content: space-between; gap: 1rem; margin-top: 0.15rem;">
					<span style="color: var(--p-color-text-subdued);">Revenue:</span>
					<strong>NRs. ${rev.toLocaleString()}</strong>
				</div>
				<div style="display: flex; justify-content: space-between; gap: 1rem; margin-top: 0.15rem;">
					<span style="color: var(--p-color-text-subdued);">Expense:</span>
					<strong>NRs. ${exp.toLocaleString()}</strong>
				</div>
				<div style="display: flex; justify-content: space-between; gap: 1rem; margin-top: 0.25rem; font-weight: 600; ${profitTone}">
					<span>Net ${profitText}:</span>
					<span>NRs. ${Math.abs(profit).toLocaleString()}</span>
				</div>
			`;

			// Calculate absolute positions relative to container
			const containerRect = container.getBoundingClientRect();
			let tooltipLeft = e.clientX - containerRect.left + 15;
			let tooltipTop = e.clientY - containerRect.top + 15;

			// Boundary checks so tooltip doesn't get clipped
			if (tooltipLeft + 160 > containerRect.width) {
				tooltipLeft = e.clientX - containerRect.left - 165;
			}
			if (tooltipTop + 100 > containerRect.height) {
				tooltipTop = containerRect.height - 100;
			}

			tooltip.style.left = `${tooltipLeft}px`;
			tooltip.style.top = `${tooltipTop}px`;
			tooltip.style.display = 'block';
		});

		this.addEventListener('mouseleave', () => {
			this.hideTooltip();
		});
	}

	private hideTooltip() {
		const tooltip = this.querySelector('.graph-tooltip') as HTMLElement;
		if (tooltip) {
			tooltip.style.display = 'none';
		}
		if (this._hoverIndex !== null) {
			this._hoverIndex = null;
			this.render();
		}
	}
}

if (!customElements.get('opm-analytics-graph')) {
	customElements.define('opm-analytics-graph', OpmAnalyticsGraph);
}
